import { useEffect, useRef, type RefObject } from 'react';

/**
 * Le temps passe sur chaque post du fil, mesure sur le telephone.
 *
 * Un post compte comme vu quand il est reste une seconde bien a l'ecran,
 * et comme lu au bout de quatre. Bien a l'ecran : aux trois cinquiemes au
 * moins, ou sur la moitie de la hauteur visible pour un post plus long que
 * l'ecran. Le compteur s'arrete des que l'application passe en arriere-plan
 * — un telephone pose sur la table n'est pas un lecteur passionne.
 *
 * Chaque post n'est annonce qu'une fois par niveau tant que l'ecran reste
 * monte : charger la page suivante reobserve les cartes sans recompter
 * celles qu'on a deja vues.
 *
 * Les cartes sont reperees par leur attribut `data-post`, sous `zone`.
 */

const BIEN_VISIBLE = 0.6;
const VU_MS = 1000;
const LU_MS = 4000;

export type NiveauDeLecture = 'vu' | 'lu';

export function useTempsDeLecture(
  zone: RefObject<HTMLElement | null>,
  actif: boolean,
  /** Les posts affiches : quand la liste change, on reobserve. */
  signature: string,
  annoncer: (postId: string, niveau: NiveauDeLecture) => void,
): void {
  const rappel = useRef(annoncer);
  rappel.current = annoncer;
  /* 1 : annonce vu, 2 : annonce lu. Survit aux pages suivantes. */
  const annonces = useRef(new Map<string, number>());
  const cumuls = useRef(new Map<string, number>());

  useEffect(() => {
    const racine = zone.current;
    if (!actif || !racine) return;

    const debuts = new Map<string, number>();

    const arreter = (id: string, instant: number) => {
      const debut = debuts.get(id);
      if (debut === undefined) return;
      debuts.delete(id);
      const total = (cumuls.current.get(id) ?? 0) + (instant - debut);
      cumuls.current.set(id, total);
      const deja = annonces.current.get(id) ?? 0;
      if (total >= VU_MS && deja < 1) {
        annonces.current.set(id, 1);
        rappel.current(id, 'vu');
      }
      if (total >= LU_MS && deja < 2) {
        annonces.current.set(id, 2);
        rappel.current(id, 'lu');
      }
    };

    const observateur = new IntersectionObserver(
      (entrees) => {
        const instant = performance.now();
        for (const entree of entrees) {
          const id = (entree.target as HTMLElement).dataset.post;
          if (!id) continue;
          const hauteur = entree.rootBounds?.height ?? 0;
          const visible =
            entree.isIntersecting &&
            (entree.intersectionRatio >= BIEN_VISIBLE ||
              (hauteur > 0 && entree.intersectionRect.height >= hauteur / 2));
          if (visible && document.visibilityState === 'visible') {
            if (!debuts.has(id)) debuts.set(id, instant);
          } else {
            arreter(id, instant);
          }
        }
      },
      { threshold: [0, 0.25, 0.5, BIEN_VISIBLE, 0.8, 1] },
    );
    racine.querySelectorAll<HTMLElement>('[data-post]').forEach((carte) => observateur.observe(carte));

    /* Les cartes qui restent a l'ecran sans bouger doivent aussi finir par compter. */
    const pouls = window.setInterval(() => {
      const instant = performance.now();
      for (const id of [...debuts.keys()]) {
        arreter(id, instant);
        debuts.set(id, instant);
      }
    }, 1000);

    const auMasquage = () => {
      if (document.visibilityState !== 'hidden') return;
      const instant = performance.now();
      for (const id of [...debuts.keys()]) arreter(id, instant);
    };
    document.addEventListener('visibilitychange', auMasquage);

    return () => {
      const instant = performance.now();
      for (const id of [...debuts.keys()]) arreter(id, instant);
      observateur.disconnect();
      window.clearInterval(pouls);
      document.removeEventListener('visibilitychange', auMasquage);
    };
  }, [zone, actif, signature]);
}
